/// <reference path="../types.d.ts" />
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Location, Action } from 'history';
import history from '../history';

type LocationChangePayload = { location: Location; action: Action };
import { Loading, UserConfig } from './action-types';
import { CMxComponents } from '@codametrix/ui-components/src/types';
import { retrieveGroups } from '../core/navigation';
import {
  displayContexts,
  fetchContexts as contextApi,
  onlyRefreshContext,
  shouldRefresh
} from './contexts.action';
import { jumpURL } from './jump';
import { applyDefaultFilters, clearAllFilters } from './cases.action';
import {
  ANALYTICS_ACTION_METADATA,
  BROADCAST_ACTION_METADATA
} from './action-constants';
import {
  AmplifyCore,
  appServiceLines,
  authenticationContext,
  commonActions,
  commonActionUtils,
  commonEnums,
  configurationUtils,
  PatientEncounterDashboardColumns,
  PatientEncounterDashboardColumnsv2,
  sessionManagement,
  uiActions,
  xcaliberActions,
  cmxRuntime
} from '@codametrix/ui-common';
import { getContextHomepage } from '../core/preferences/preferences';
import { saveFormData } from './account-settings';
import { NEW_DESIGN_SUFFIX } from '../core/utils/newDesign';
import { ToastVariants } from '../core/enums';

const { shimContext } = commonActionUtils;
const { activityMonitor } = sessionManagement;

const UNDEFINED_PREF_CHOICE = undefined;

const { CMxApplication, ParentWindow, Router, UserInterface } = commonActions;

const dismissModal = createAction(UserInterface.DISMISS_MODAL_MESSAGE, () => ({
  payload: undefined as void,
  meta: { ...BROADCAST_ACTION_METADATA }
}));

// showModalMessage can not be broadcast atm bc there are functions as part of the payload
// and this runs into structured cloning issues.
const showModalMessage = createAction<CMxComponents.ModalProps>(
  UserInterface.SHOW_MODAL_MESSAGE
);

const logout = createAction(UserInterface.LOGOUT, () => ({
  payload: undefined as void,
  meta: { ...ANALYTICS_ACTION_METADATA, ...BROADCAST_ACTION_METADATA }
}));

const changeAdminTab = createAction<CMxComponents.TabDef[]>(
  UserInterface.ADMIN_TAB_CHANGE
);

const onLocationChange = createAction<LocationChangePayload>(
  Router.LOCATION_CHANGE
);

const navigate = createAction(
  UserInterface.NAVIGATE,
  (payload: CMxAPI.NavAction | CMxComponents.TabDef) => ({
    payload,
    meta: { user: true }
  })
);

const parentDidNavigate = createAction<boolean>(
  ParentWindow.PARENT_NAVIGATION
);

const configAvailable = createAction<CMxAPI.UserConfig>(
  UserConfig.CONFIG_AVAILABLE
);

const dismissFeedback = createAction<boolean>(CMxApplication.DISMISS_FEEDBACK);

const loading = createAction<boolean>(CMxApplication.LOADING);

const replaceUIState = createAction<CMx.InterfaceState>(
  UserInterface.REPLACE_UI_STATE
);

const loadingAction = createAction<CMx.LoadingPayload>(Loading.LOADING_ACTION);

const setActiveUser = createAction(
  UserInterface.SET_ACTIVE_USER,
  (payload: CMxAPI.User) => ({
    payload,
    meta: { ...ANALYTICS_ACTION_METADATA }
  })
);

const showFeedback = createAction<CMxCommonApp.Feedback>(
  CMxApplication.FEEDBACK
);

const detailTogglePHI = createAction<CMx.PHIMask>(
  UserInterface.DETAIL_TOGGLE_PHI
);

const setHomepagePreference = createAction<CMxAPI.Preference | undefined>(
  UserInterface.SET_DEFAULT_HOMEPAGE
);

const setBackToWorklistPath = createAction<{ path: string }>(
  UserInterface.SET_BACK_TO_WORKLIST_PATH
);

const setPropertyConfig = createAction<
  CMxCommonApp.PatientInformationConfiguration
>('SET_PROPERTY_CONFIG');

const showFeedbackError = (props: Partial<CMxCommonApp.Feedback>) => {
  const arg = {
    ...props,
    className: `${props.className} toast-error`,
    status: ToastVariants.ERROR,
    id: Date.now()
  } as CMxCommonApp.Feedback;
  return showFeedback(arg);
};

const showFeedbackSuccess = (props: Partial<CMxCommonApp.Feedback>) => {
  const arg = {
    ...props,
    className: `${props.className} toast-success`,
    status: ToastVariants.SUCCESS,
    id: Date.now()
  } as CMxCommonApp.Feedback;
  return showFeedback(arg);
};

const showFeedbackWorking = (props: Partial<CMxCommonApp.Feedback>) => {
  const arg = {
    ...props,
    className: `${props.className} feedback-working toast-success`,
    status: ToastVariants.INFO,
    id: Date.now()
  } as CMxCommonApp.Feedback;
  return showFeedback(arg);
};

const hasEnabled = (option: CMxAPI.NavAction) => option.enabled;
const showFeedbackWarning = (props: Partial<CMxCommonApp.Feedback>) => {
  const arg = {
    ...props,
    className: `${props.className} feedback-warning toast-warning`,
    status: ToastVariants.WARNING,
    id: Date.now()
  } as CMxCommonApp.Feedback;
  return showFeedback(arg);
};

const firstGroup = (context: CMx.Context) => {
  if (context === null || context.activeContext === null) {
    return false;
  } else {
    const groups = retrieveGroups(context.activeContext, '*');
    const firstGroup = groups.find(
      group => group.key !== 'search' && group.options.some(hasEnabled)
    );
    if (!firstGroup) {
      return false;
    }
    return firstGroup.options.find(hasEnabled);
  }
};

const toggleDrawer = createAction<boolean>(CMxApplication.DRAWER);

const toggleDrawerContent = createAction<boolean>(
  CMxApplication.DRAWER_CONTENT
);

/* The "context" within the navFields object doesn't seem to contain serviceLineName. But if
   we manually get the state and look for the uiContext it does have the serviceLineName.
   Below was reworked to get CDMX-2845 across the finish line and should be investigated further
   and documented. */
const navigateContext = createAsyncThunk<
  void,
  CMx.NavData,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.NAVIGATE_CONTEXT,
  async (navFields, { dispatch, getState }) => {
    const { context, homepagePreference } = navFields;

    const state = getState() as any;
    const uiContext: CMx.Context = state.ui?.context ?? context;
    const serviceLineName = state.ui?.context?.serviceLineName ?? '';

    const isNewDesign = cmxRuntime.runtimeOverrides?.newAdminDesign;
    let defaultHomepage;
    if (uiContext.activeContext)
      defaultHomepage = getContextHomepage(
        uiContext.activeContext?.tenantId,
        homepagePreference
      );

    const hasProviderDirectory = context.activeContext
      ? AmplifyCore.benchEditable.isResourceAllowed(
          uiContext.activeContext!,
          'PROVIDER_DIRECTORY',
          serviceLineName
        )
      : false;

    if (hasProviderDirectory) {
      xcaliberActions.loadXCaliber(dispatch as any);
    }

    dispatch(clearAllFilters() as any);
    dispatch(applyDefaultFilters(uiContext) as any);
    const option = firstGroup(uiContext);
    if (!option) {
      history.replace(`/cmx/`);
    } else if (defaultHomepage) {
      dispatch(
        saveFormData({
          defaultOrg: UNDEFINED_PREF_CHOICE,
          defaultServiceLine: UNDEFINED_PREF_CHOICE,
          defaultHomepage
        })
      );
      history.replace(
        `/cmx/${defaultHomepage.path}${isNewDesign ? NEW_DESIGN_SUFFIX : ''}`
      );
    } else {
      dispatch(navigate(option));
      dispatch(setBackToWorklistPath(option));
      history.replace(`/cmx/${option.path}${isNewDesign ? NEW_DESIGN_SUFFIX : ''}`);
    }
  }
);

const openWindow = createAction<CMxCommonApp.WindowOpenArguments>(
  UserInterface.OPEN_CHILD_WINDOW
);

const navigateAndReload = (context: CMx.Context, dispatch: any) => {
  const option = firstGroup(context);
  if (option) {
    window.location.replace(`/cmx/${option.path}`);
  }
};

const fetchContexts = createAsyncThunk<
  CMx.Context,
  CMx.NavData | null,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.BOOTSTRAP,
  async (navData, { dispatch }): Promise<CMx.Context> => {
    const { context: optContext, homepagePreference } = navData ?? {};
    // in some cases the activeContext will already have been
    // determined.  if this is the case, then simply return
    // the context.  if the user needs to select the context
    // later we should provision this in the UI.
    if (optContext && optContext.activeContext !== null) {
      authenticationContext.setContextId(optContext.activeContextId);
      dispatch(
        uiActions.saveServiceLineCapability(
          optContext?.activeContext?.serviceLineName || ''
        )
      );
      dispatch(uiActions.awaitingResources(false));
      return optContext;
    } else {
      const userContexts = await contextApi(dispatch as any);

      const shimmed = shimContext(userContexts);
      const { contexts, user } = userContexts;

      dispatch(setActiveUser(user));

      if (shimmed.activeContext === null && contexts.length > 1) {
        dispatch(displayContexts(contexts));
      } else {
        const jumped = jumpURL(shimmed, dispatch as any);
        if (!jumped) {
          dispatch(uiActions.chooseContext(shimmed));
          dispatch(navigateContext({ context: shimmed, homepagePreference }) as any);
          dispatch(loading(false));
        }
      }
      dispatch(
        uiActions.saveServiceLineCapability(
          shimmed?.activeContext?.serviceLineName || ''
        ) as any
      );
      return shimmed;
    }
  }
);

type submitArgs = {
  name: string;
  params?: { [key: string]: any };
};

const modalSubmit = createAsyncThunk<
  void,
  submitArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.MODAL_SUBMIT,
  async (args, { dispatch }) => {
    const { name, params } = args;

    switch (name) {
      case UserInterface.LOGOUT:
        dispatch(logout());
        if (params?.isSSOUser && params?.orgCode?.length) {
          history.replace(`/sso/${params?.orgCode}`);
        } else {
          history.push(`/`);
        }
        break;
      case UserInterface.REFRESH:
        // signal to other windows to start the refresh process
        // get new token.
        dispatch(shouldRefresh());
        dispatch(dismissModal());
        await dispatch(
          onlyRefreshContext({
            organizationId: `${authenticationContext.getContextId()}`
          }) as any
        );
        activityMonitor.shouldRefresh(undefined);
        break;
      default:
        break;
    }
  }
);

const fetchCaseListColumnConfiguration = createAsyncThunk<
  any,
  { tenantId: string; serviceLine: string },
  { rejectValue: CMxCommonApp.SubmitError }
>('FETCH_COLUMN_CONFIGURATION', async (params, _thunkAPI) => {
  const { tenantId, serviceLine } = params;
  try {
    const jsonValue = await configurationUtils.fetchMergedConfigurationJsonValue(
      'caselist-col-order',
      tenantId,
      serviceLine
    );

    // Only return entries where config.field is not null/undefined
    if (!jsonValue || typeof jsonValue !== 'object') {
      return {};
    }
    return Object.fromEntries(
      Object.entries(jsonValue).filter(
        ([, config]) => (config as { field?: string }).field != null
      )
    );
  } catch (error) {
    throw error;
  }
});

const fetchCaseDetailsLayoutConfiguration = createAsyncThunk<
  void,
  {
    tenantId: string;
    serviceLine: string;
    hasAmplifyCaseRunV2Access?: boolean;
  },
  { rejectValue: CMxCommonApp.SubmitError }
>('FETCH_CASE_DETAILS_LAYOUT_CONFIGURATION', async (params, { dispatch }) => {
  const { tenantId, serviceLine, hasAmplifyCaseRunV2Access = false } = params;
  const choiceName = serviceLine ?? '';

  const { ServiceLines } = commonEnums;

  let configName = choiceName;
  if (appServiceLines.isIPC(choiceName)) {
    configName = ServiceLines.IPC_LONG_NAME;
  }

  const slName: string = `${configName}`.toLowerCase().replace(/\s/g, '');

  const encounterColumns = hasAmplifyCaseRunV2Access
    ? PatientEncounterDashboardColumnsv2
    : PatientEncounterDashboardColumns;

  // Check if slName exists in the chosen object - this is the fallback
  const fallbackPropertyConfig =
    slName in encounterColumns ? encounterColumns[slName] : null;

  try {
    let jsonValue = null;
    if (hasAmplifyCaseRunV2Access) {
      jsonValue = await configurationUtils.fetchMergedConfigurationJsonValue(
        'demographics-panel-layout',
        tenantId,
        serviceLine
      );
    } else {
      jsonValue = await configurationUtils.fetchMergedConfigurationJsonValue(
        'case-details-layout',
        tenantId,
        serviceLine
      );
    }

    if (jsonValue && Object.keys(jsonValue).length > 0) {
      // Convert structure to one the UI expects
      const layoutConfig: CMxCommonApp.PatientInformationConfiguration = {
        patient: Object.values(jsonValue.patient || {})
          .filter((value: any) => value !== null)
          .map((value: any) => {
            return {
              ...value
            };
          }),
        summary: Object.values(jsonValue.summary || {})
          .filter((value: any) => value !== null)
          .map((value: any) => {
            return {
              ...value
            };
          }),
        expanded: Object.values(jsonValue.expanded || {})
          .filter((value: any) => value !== null)
          .map((value: any) => {
            return {
              ...value
            };
          })
      };

      dispatch(setPropertyConfig(layoutConfig));
    } else if (fallbackPropertyConfig) {
      // Use fallback if no dynamic config found
      dispatch(setPropertyConfig(fallbackPropertyConfig));
    }
  } catch (error) {
    console.error('Error fetching case details layout configuration:', error);
    // Use fallback on error
    if (fallbackPropertyConfig) {
      dispatch(setPropertyConfig(fallbackPropertyConfig));
    }
  }
});

export {
  replaceUIState,
  fetchContexts,
  configAvailable,
  toggleDrawer,
  toggleDrawerContent,
  logout,
  loading,
  loadingAction,
  navigate,
  navigateContext,
  navigateAndReload,
  detailTogglePHI,
  showModalMessage,
  onLocationChange,
  dismissModal,
  dismissFeedback,
  showFeedback,
  showFeedbackError,
  showFeedbackSuccess,
  showFeedbackWorking,
  showFeedbackWarning,
  setBackToWorklistPath,
  changeAdminTab,
  openWindow,
  parentDidNavigate,
  setActiveUser,
  setHomepagePreference,
  setPropertyConfig,
  modalSubmit,
  fetchCaseListColumnConfiguration,
  fetchCaseDetailsLayoutConfiguration
};
